import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { clsx } from "clsx";
import {
  type ComponentPropsWithoutRef,
  createContext,
  type ElementRef,
  forwardRef,
  useContext,
  useMemo,
  useState,
} from "react";

import useHasHover from "common/helpers/useHasHover";

import type React from "react";

const TooltipPortal = TooltipPrimitive.Portal;
const TooltipProvider = TooltipPrimitive.Provider;

type TooltipTriggerContextType = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const TooltipTriggerContext = createContext<TooltipTriggerContextType>({
  isOpen: false,
  setIsOpen: () => {},
});

const Tooltip = forwardRef<
  ElementRef<typeof TooltipPrimitive.Root>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Root> & {
    disabled?: boolean;
  }
>(({ children, disabled, ...props }) => {
  const [isOpen, setIsOpen] = useState(props.defaultOpen ?? false);
  const hasHover = useHasHover();
  const contextValue = useMemo(() => ({ isOpen, setIsOpen }), []);

  return (
    <TooltipPrimitive.Root
      delayDuration={hasHover ? props.delayDuration : 0}
      onOpenChange={(e) => {
        if (!disabled) {
          setIsOpen(e);
        }
      }}
      open={isOpen}
    >
      <TooltipTriggerContext.Provider value={contextValue}>
        {children}
      </TooltipTriggerContext.Provider>
    </TooltipPrimitive.Root>
  );
});
Tooltip.displayName = TooltipPrimitive.Root.displayName;

const TooltipTrigger = forwardRef<
  ElementRef<typeof TooltipPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ children, ...props }, ref) => {
  const hasHover = useHasHover();
  const { setIsOpen } = useContext(TooltipTriggerContext);

  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      {...props}
      onClick={
        hasHover
          ? props.onClick
          : (e) => {
              e.preventDefault();
              setIsOpen(true);
              if (props.onClick) {
                props.onClick(e);
              }
            }
      }
    >
      {children}
    </TooltipPrimitive.Trigger>
  );
});
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = forwardRef<
  ElementRef<typeof TooltipPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, children, align, ...props }, ref) => (
  <TooltipPortal>
    <TooltipPrimitive.Content
      align={align}
      ref={ref}
      sideOffset={sideOffset}
      collisionPadding={8}
      alignOffset={align === "start" ? -16 : 0}
      className={clsx(
        "p-4 w-fit max-w-56 z-tooltip rounded-2xl bg-black-950 text-white text-xs",
        "animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    >
      {children}
      <TooltipPrimitive.Arrow style={{ width: "initial", height: "initial" }} />
    </TooltipPrimitive.Content>
  </TooltipPortal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  TooltipPortal,
};
